import * as React from 'react';
import { useHealthMetricValuesV3 } from '#components/charts/core/range-graph/utils/use-health-metric-values.ts';
import { shapes } from '#components/charts/core/xychart/primitives/Shape.tsx';
import { MasonryBarGraphIntervalSelector } from '#components/charts/core/bar-graph/index.ts';
import { MasonryRangeGraphIntervalSelector } from '#components/charts/core/range-graph/masonry-range-graph-interval-selector.tsx';
import { MasonryLineGraphIntervalSelector } from '#components/charts/core/line-graph/masonry-line-graph-interval-selector.tsx';
import { MultipleMeasurementsTooltipV3 } from '#components/charts/core/chart-tooltip/v3/multiple-measurements-tooltipV3.tsx';
import { SingleMeasurementTooltipV3 } from '#components/charts/core/chart-tooltip/v3/single-measurement-tooltipV3.tsx';
import { useDatumLabel } from '#pages/details3/hooks/use-datum-label.ts';
import type { MasonryChart } from '#types/masonry-chart.type.ts';
import { convertMasonryChartToHealthMetricData } from './utils/convert-masonry-chart-to-health-metric-data';

type ChartProps = MasonryChart;

export function Chart({
  chartType,
  interval,
  aggregationBucketType,
  screenReaderTitle,
  screenReaderDescription,
  yAxisLabel,
  yAxisMax,
  xAxisLabel,
  dataset,
  dataPointTypes,
}: ChartProps) {
  const generateDatumLabel = useDatumLabel();

  const chartData = React.useMemo(
    () => convertMasonryChartToHealthMetricData(dataset, dataPointTypes),
    [dataset, dataPointTypes],
  );

  const data = useHealthMetricValuesV3(chartData, aggregationBucketType);

  switch (chartType) {
    case 'bar': {
      return (
        <MasonryBarGraphIntervalSelector
          interval={interval}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value as number,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={yAxisLabel}
          axisBottomLabel={xAxisLabel}
          ariaDatumLabel={generateDatumLabel}
          renderTooltip={(datum) => (
            <SingleMeasurementTooltipV3
              datum={datum}
              interval={interval}
              aggregationBucketType={aggregationBucketType}
            />
          )}
          yAxisMax={yAxisMax}
        />
      );
    }
    case 'line': {
      return (
        <MasonryLineGraphIntervalSelector
          interval={interval}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value as number,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={yAxisLabel}
          axisBottomLabel={xAxisLabel}
          ariaDatumLabel={generateDatumLabel}
          renderTooltip={(datum) => (
            <SingleMeasurementTooltipV3
              datum={datum}
              interval={interval}
              aggregationBucketType={aggregationBucketType}
            />
          )}
          yAxisMax={yAxisMax}
        />
      );
    }
    case 'range': {
      return (
        <MasonryRangeGraphIntervalSelector
          interval={interval}
          aggregationBucketType={aggregationBucketType}
          data={data}
          accessors={{
            xAccessor: (datum) => datum.timestamp,
            yAccessor: (datum) => datum.value,
          }}
          title={screenReaderTitle}
          description={screenReaderDescription}
          axisLeftLabel={yAxisLabel}
          axisBottomLabel={xAxisLabel}
          ariaDatumLabel={generateDatumLabel}
          getDatumSymbol={(datum) => shapes[datum.metric.data.order]}
          renderTooltip={(datum) => (
            <MultipleMeasurementsTooltipV3
              data={datum}
              interval={interval}
              aggregationBucketType={aggregationBucketType}
            />
          )}
          yAxisMax={yAxisMax}
        />
      );
    }
    default:
      throw new Error(`Unsupported chart type ${chartType} was specified`);
  }
}
